/**
 * Блок «Верифицировать аккаунт» в профиле (#1296): статусы способов входа и возврат из Яндекса.
 *
 * Статусы считает сервер: `yandexLinked` / `vkLinked` (привязан провайдерский id) и
 * `emailIsPlaceholder` + `emailVerified` (настоящий адрес, подтверждённый кодом). Сами id наружу не отдаются.
 */
type VerifyUser =
    | {
          email?: string | null;
          emailIsPlaceholder?: boolean;
          emailVerified?: boolean;
          yandexLinked?: boolean;
          vkLinked?: boolean;
      }
    | null
    | undefined;

export type VerifyMethod = "yandex" | "vk" | "email";

/** Адрес настоящий (не заглушка `*.oauth`) и подтверждён. Ожидающий адрес (`pendingEmail`) не считается. */
export function isEmailVerified(user: VerifyUser): boolean {
    return Boolean(user?.email) && !user?.emailIsPlaceholder && user?.emailVerified !== false;
}

export function verifyStatuses(user: VerifyUser): Record<VerifyMethod, boolean> {
    return {
        yandex: Boolean(user?.yandexLinked),
        vk: Boolean(user?.vkLinked),
        email: isEmailVerified(user),
    };
}

const STATUS_LABELS: Array<[VerifyMethod, string]> = [
    ["email", "Email"],
    ["yandex", "Yandex ID"],
    ["vk", "VK ID"],
];

/** Подзаголовок строки «Верифицировать аккаунт» в профиле: верифицированные способы или призыв. */
export function verifySubtitle(user: VerifyUser): string {
    const status = verifyStatuses(user);
    const done = STATUS_LABELS.filter(([key]) => status[key]).map(([, label]) => label);
    return done.length ? done.join(" · ") : "Подтвердите аккаунт";
}

/** Адрес страницы «Верифицировать аккаунт» (режим профиля): сюда возвращается привязка Яндекса. */
export const VERIFY_PAGE_PATH = "/app/profile?verify=1";

/** Нужно ли открыть страницу верификации (возврат из Яндекса или переход по ссылке) по строке запроса. */
export function wantsVerifyPage(search: string): boolean {
    return new URLSearchParams(search).get("verify") === "1";
}

// ── Возврат из Яндекса после привязки ────────────────────────────────────────

const YANDEX_LINK_KEY = "rla-yandex-link";
const YANDEX_LINK_TTL_MS = 15 * 60 * 1000;

type KeyValueStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

interface PendingYandexLink {
    state: string;
    returnPath: string;
    startedAt: number;
}

function readPending(storage: KeyValueStorage, now: number): PendingYandexLink | null {
    try {
        const raw = storage.getItem(YANDEX_LINK_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw) as Partial<PendingYandexLink>;
        if (typeof data.state !== "string" || !data.state || typeof data.startedAt !== "number") return null;
        if (now - data.startedAt > YANDEX_LINK_TTL_MS) return null;
        return { state: data.state, returnPath: typeof data.returnPath === "string" ? data.returnPath : "", startedAt: data.startedAt };
    } catch {
        return null;
    }
}

/** Запоминает начало привязки и отдаёт одноразовый `state` для запроса к Яндексу. */
export function beginYandexLink(storage: KeyValueStorage, returnPath: string, state: string, now = Date.now()): string {
    storage.setItem(YANDEX_LINK_KEY, JSON.stringify({ state, returnPath, startedAt: now }));
    return state;
}

/** Только внутренний путь приложения: чужой адрес в sessionStorage не должен стать редиректом. */
function safeReturnPath(path: string): string {
    return path.startsWith("/") && !path.startsWith("//") ? path : "";
}

export type YandexReturn =
    | { kind: "none" }
    | { kind: "link"; code: string; returnPath: string }
    | { kind: "cancelled"; returnPath: string }
    /** В адресе есть `state`, но записи привязки нет (протухла, другая вкладка, подделка): кодом не входим. */
    | { kind: "stale" };

/**
 * Разбирает возврат из Яндекса (#1296). Обычный вход `state` не посылает, поэтому любой `state`
 * в адресе -- это возврат привязки: код принимается только при совпавшем `state`, а без записи
 * (`stale`) он не должен стать входом. Запись удаляется при совпавшем `state` (одноразовость).
 */
export function readYandexReturn(storage: KeyValueStorage, search: string, now = Date.now()): YandexReturn {
    const params = new URLSearchParams(search);
    const state = params.get("state");
    if (!state) return { kind: "none" };
    const pending = readPending(storage, now);
    if (!pending || pending.state !== state) return { kind: "stale" };
    storage.removeItem(YANDEX_LINK_KEY);
    const returnPath = safeReturnPath(pending.returnPath);
    if (params.get("error")) return { kind: "cancelled", returnPath };
    const code = params.get("code");
    return code ? { kind: "link", code, returnPath } : { kind: "none" };
}

/** Человекочитаемый отказ привязки по коду ответа сервера. */
export function linkErrorMessage(code: string | null | undefined, fallback: string): string {
    if (code === "PROVIDER_TAKEN") return "Этот аккаунт уже привязан к другому профилю.";
    if (code === "PROVIDER_ALREADY_LINKED") return "К вашему аккаунту уже привязан другой аккаунт этого сервиса.";
    return fallback;
}

/**
 * #1303: чей это возврат на `/app?...`. Один адрес принимает возвраты Яндекса (вход и привязка) и VK ID
 * (`code` + `state` + `device_id`); код одного провайдера нельзя отдавать обработчику другого --
 * обмен упадёт 400, а у авторизованного вход подменил бы сессию.
 * - `vk`: есть `device_id` (его присылает только VK ID) -- Яндекс-обработчики не трогают;
 * - `yandex-link`: есть `state` (его ставит только привязка Яндекса, #1296);
 * - `yandex-login`: `code` без `state` и `device_id`, сессии нет;
 * - `none`: остальное, в том числе `code` без `state` у уже авторизованного (входом не становится).
 */
export type OauthReturnOwner = "none" | "vk" | "yandex-link" | "yandex-login";

export function classifyOauthReturn(search: string, isAuthenticated: boolean): OauthReturnOwner {
    const params = new URLSearchParams(search);
    if (params.get("device_id")) return "vk";
    if (params.get("state")) return "yandex-link";
    if (params.get("code") && !isAuthenticated) return "yandex-login";
    return "none";
}

/** Куда вернуть после Яндекса, если экран не передал свой путь: всегда страница верификации, не «текущий путь» (в настройках это был бы просто профиль). */
export function yandexReturnPathFor(returnPath?: string): string {
    return returnPath || VERIFY_PAGE_PATH;
}

// ── Возврат из VK ID после привязки ──────────────────────────────────────────
// VK ID SDK в режиме Callback на части устройств (мобильные браузеры, WebView) вместо всплывающего окна
// уводит всю страницу на redirectUrl (`/app?code&state&device_id`). Запоминаем, откуда начали,
// чтобы вернуть человека на ту же страницу, а не оставить на ленте.

const VK_LINK_KEY = "rla-vk-link";

/** Одноразовый код VK, переданный со шлюза странице верификации через состояние роутера (в адрес он не попадает). */
export interface VkReturn {
    code: string;
    deviceId: string;
}

export function beginVkLink(storage: KeyValueStorage, returnPath: string, now = Date.now()): void {
    storage.setItem(VK_LINK_KEY, JSON.stringify({ returnPath, startedAt: now }));
}

/** Разбирает возврат VK: нужен `device_id`, `code` и живая запись привязки (иначе чужой/старый адрес). */
export function readVkReturn(
    storage: KeyValueStorage,
    search: string,
    now = Date.now(),
): { returnPath: string; vkReturn: VkReturn } | null {
    const params = new URLSearchParams(search);
    const code = params.get("code");
    const deviceId = params.get("device_id");
    if (!code || !deviceId) return null;
    try {
        const raw = storage.getItem(VK_LINK_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw) as { returnPath?: unknown; startedAt?: unknown };
        if (typeof data.startedAt !== "number" || now - data.startedAt > YANDEX_LINK_TTL_MS) return null;
        storage.removeItem(VK_LINK_KEY);
        const returnPath = safeReturnPath(typeof data.returnPath === "string" ? data.returnPath : "") || VERIFY_PAGE_PATH;
        return { returnPath, vkReturn: { code, deviceId } };
    } catch {
        return null;
    }
}
